import type { TypedSupabaseClient } from '../client';
import type { TablesInsert, TablesUpdate } from '../../../supabase-client/src/database.types';
import type {
  IArticleRepository,
  IArticleCategoryRepository,
  IArticleFamilyRepository,
  IArticleBrandRepository,
  Article,
  ArticleBrand,
  ArticleCategory,
  ArticleFamily,
  ArticleFilters,
  CreateArticleInput,
  CreateArticleBrandInput,
  CreateArticleCategoryInput,
  UpdateArticleInput,
} from '../../../interfaces/src';
import { consumeEntityCode, peekEntityCode } from './entity-codes';

export class SupabaseArticleCategoryRepository implements IArticleCategoryRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(): Promise<ArticleCategory[]> {
    const { data, error } = await this.supabase.from('article_categories').select('*').order('name');
    if (error) throw error;
    return (data ?? []).map(mapCategory);
  }

  async create(input: CreateArticleCategoryInput): Promise<ArticleCategory> {
    const tenantId = await resolveCurrentTenantId(this.supabase);
    const row: TablesInsert<'article_categories'> = {
      tenant_id: tenantId,
      name: input.name,
      parent_id: input.parentId ?? null,
      image_url: input.imageUrl ?? null,
    };
    const { data, error } = await this.supabase
      .from('article_categories')
      .insert(row)
      .select()
      .single();
    if (error) throw error;
    return mapCategory(data as Record<string, unknown>);
  }

  async update(id: string, input: Partial<CreateArticleCategoryInput>): Promise<ArticleCategory> {
    const mapped: TablesUpdate<'article_categories'> = {};
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.parentId !== undefined) mapped['parent_id'] = input.parentId;
    if (input.imageUrl !== undefined) mapped['image_url'] = input.imageUrl;

    const { data, error } = await this.supabase
      .from('article_categories')
      .update(mapped)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapCategory(data as Record<string, unknown>);
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('article_categories').delete().eq('id', id);
    if (error) throw error;
  }
}

export class SupabaseArticleRepository implements IArticleRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async peekNextCode(): Promise<string> {
    return peekEntityCode(this.supabase, 'article');
  }

  async peekNextBarcode(): Promise<string> {
    return peekEntityCode(this.supabase, 'barcode');
  }

  async list(filters?: ArticleFilters): Promise<Article[]> {
    let query = this.supabase.from('articles').select('*');
    const search = filters?.search?.trim();
    if (search) {
      const escaped = escapePostgrestFilterValue(search);
      query = query.or(`name.ilike.%${escaped}%,code.ilike.%${escaped}%,barcode.ilike.%${escaped}%,description.ilike.%${escaped}%`);
    }
    if (filters?.categoryId) {
      query = query.eq('category_id', filters.categoryId);
    }
    query = applyLooseTextFilter(query, 'family', filters?.family);
    query = applyLooseTextFilter(query, 'brand', filters?.brand);
    if (filters?.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive);
    }
    if (filters?.isInvoiceable !== undefined) {
      query = query.eq('is_invoiceable', filters.isInvoiceable);
    }
    query = query.order('name');
    if (filters?.limit && filters.limit > 0) {
      const offset = Math.max(0, filters.offset ?? 0);
      query = query.range(offset, offset + filters.limit - 1);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map(mapArticle);
  }

  async count(filters?: ArticleFilters): Promise<number> {
    let query = this.supabase.from('articles').select('id', { count: 'exact', head: true });
    const search = filters?.search?.trim();
    if (search) {
      const escaped = escapePostgrestFilterValue(search);
      query = query.or(`name.ilike.%${escaped}%,code.ilike.%${escaped}%,barcode.ilike.%${escaped}%,description.ilike.%${escaped}%`);
    }
    if (filters?.categoryId) {
      query = query.eq('category_id', filters.categoryId);
    }
    query = applyLooseTextFilter(query, 'family', filters?.family);
    query = applyLooseTextFilter(query, 'brand', filters?.brand);
    if (filters?.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive);
    }
    if (filters?.isInvoiceable !== undefined) {
      query = query.eq('is_invoiceable', filters.isInvoiceable);
    }
    const { count, error } = await query;
    if (error) throw error;
    return count ?? 0;
  }

  async getById(id: string): Promise<Article | null> {
    const { data, error } = await this.supabase.from('articles').select('*').eq('id', id).single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapArticle(data);
  }

  async create(input: CreateArticleInput): Promise<Article> {
    const tenantId = await resolveCurrentTenantId(this.supabase);
    const code = input.code && input.code.trim() !== ''
      ? input.code.trim()
      : await consumeEntityCode(this.supabase, 'article');
    // Barcode : auto-génération explicite via flag, sinon on prend la valeur
    // saisie telle quelle (ou null si vide).
    let barcode: string | null;
    if (input.autoGenerateBarcode) {
      barcode = await consumeEntityCode(this.supabase, 'barcode');
    } else {
      const rawBarcode = input.barcode?.trim();
      barcode = rawBarcode && rawBarcode !== '' ? rawBarcode : null;
    }
    const { data, error } = await this.supabase
      .from('articles')
      .insert({
        tenant_id: tenantId,
        code,
        name: input.name,
        sale_price_ht: input.salePriceHt ?? 0,
        sale_price_ttc: input.salePriceTtc ?? 0,
        barcode,
        description: input.description,
        category_id: input.categoryId,
        family: input.family,
        sub_family: input.subFamily,
        brand: input.brand,
        unit: input.unit ?? 'pcs',
        purchase_price_ht: input.purchasePriceHt ?? 0,
        supplier_discount: input.supplierDiscount ?? 0,
        vat_rate: input.vatRate ?? 19,
        stock_min: input.stockMin ?? 0,
        initial_stock: input.initialStock ?? 0,
        images: input.images ?? [],
        image_url: input.imageUrl,
        is_active: input.isActive ?? true,
        is_invoiceable: input.isInvoiceable ?? true,
      })
      .select()
      .single();
    if (error) throw error;
    return mapArticle(data);
  }

  async update(id: string, input: UpdateArticleInput): Promise<Article> {
    const mapped: TablesUpdate<'articles'> = {};
    if (input.code !== undefined) mapped['code'] = input.code;
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.salePriceHt !== undefined) mapped['sale_price_ht'] = input.salePriceHt;
    if (input.salePriceTtc !== undefined) mapped['sale_price_ttc'] = input.salePriceTtc;
    if (input.barcode !== undefined) mapped['barcode'] = input.barcode;
    if (input.description !== undefined) mapped['description'] = input.description;
    if (input.categoryId !== undefined) mapped['category_id'] = input.categoryId;
    if (input.family !== undefined) mapped['family'] = input.family;
    if (input.subFamily !== undefined) mapped['sub_family'] = input.subFamily;
    if (input.brand !== undefined) mapped['brand'] = input.brand;
    if (input.unit !== undefined) mapped['unit'] = input.unit;
    if (input.purchasePriceHt !== undefined) mapped['purchase_price_ht'] = input.purchasePriceHt;
    if (input.supplierDiscount !== undefined) mapped['supplier_discount'] = input.supplierDiscount;
    if (input.vatRate !== undefined) mapped['vat_rate'] = input.vatRate;
    if (input.stockMin !== undefined) mapped['stock_min'] = input.stockMin;
    if (input.initialStock !== undefined) mapped['initial_stock'] = input.initialStock;
    if (input.images !== undefined) mapped['images'] = input.images;
    if (input.imageUrl !== undefined) mapped['image_url'] = input.imageUrl;
    if (input.isActive !== undefined) mapped['is_active'] = input.isActive;
    if (input.isInvoiceable !== undefined) mapped['is_invoiceable'] = input.isInvoiceable;

    const { data, error } = await this.supabase
      .from('articles')
      .update(mapped)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapArticle(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('articles').delete().eq('id', id);
    if (error) throw error;
  }
}

function escapePostgrestFilterValue(value: string): string {
  return value.replace(/[%*,()]/g, '\\$&');
}

// `family` and `brand` columns are loose VARCHARs that can store either a
// UUID (new rows) or a raw name (legacy/imported rows). When callers send
// an array we treat it as "match any of these candidate values" via `.in()`.
function applyLooseTextFilter<Q extends { eq: (col: string, value: string) => Q; in: (col: string, values: string[]) => Q }>(
  query: Q,
  column: string,
  value: string | string[] | undefined,
): Q {
  if (value === undefined) return query;
  if (Array.isArray(value)) {
    const cleaned = value.filter((v) => typeof v === 'string' && v.length > 0);
    if (cleaned.length === 0) return query;
    if (cleaned.length === 1) return query.eq(column, cleaned[0]!);
    return query.in(column, cleaned);
  }
  if (value === '') return query;
  return query.eq(column, value);
}

function mapArticle(row: Record<string, unknown>): Article {
  return {
    id: row['id'] as string,
    code: row['code'] as string,
    barcode: (row['barcode'] as string) ?? null,
    name: row['name'] as string,
    description: (row['description'] as string) ?? null,
    categoryId: (row['category_id'] as string) ?? null,
    family: (row['family'] as string) ?? null,
    subFamily: (row['sub_family'] as string) ?? null,
    brand: (row['brand'] as string) ?? null,
    unit: (row['unit'] as string) ?? 'pcs',
    purchasePriceHt: (row['purchase_price_ht'] as number) ?? 0,
    supplierDiscount: (row['supplier_discount'] as number) ?? 0,
    salePriceHt: (row['sale_price_ht'] as number) ?? 0,
    salePriceTtc: (row['sale_price_ttc'] as number) ?? 0,
    vatRate: (row['vat_rate'] as number) ?? 19,
    stockMin: (row['stock_min'] as number) ?? 0,
    initialStock: (row['initial_stock'] as number) ?? 0,
    images: (row['images'] as string[]) ?? [],
    imageUrl: (row['image_url'] as string) ?? null,
    isActive: (row['is_active'] as boolean) ?? true,
    isInvoiceable: (row['is_invoiceable'] as boolean) ?? true,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapCategory(r: Record<string, unknown>): ArticleCategory {
  return {
    id: r['id'] as string,
    name: r['name'] as string,
    parentId: (r['parent_id'] as string) ?? null,
    imageUrl: (r['image_url'] as string) ?? null,
  };
}

// ── Families ──────────────────────────────────────────────────────

export class SupabaseArticleFamilyRepository implements IArticleFamilyRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(): Promise<ArticleFamily[]> {
    const { data, error } = await this.supabase.from('article_families').select('*').order('name');
    if (error) throw error;
    return (data ?? []).map((r: Record<string, unknown>) => ({ id: r['id'] as string, name: r['name'] as string }));
  }

  async create(name: string): Promise<ArticleFamily> {
    const tenantId = await resolveCurrentTenantId(this.supabase);
    const { data, error } = await this.supabase.from('article_families').insert({ tenant_id: tenantId, name }).select().single();
    if (error) throw error;
    const row = data as Record<string, unknown>;
    return { id: row['id'] as string, name: row['name'] as string };
  }

  async update(id: string, input: { name: string }): Promise<ArticleFamily> {
    const { data, error } = await this.supabase
      .from('article_families')
      .update({ name: input.name })
      .eq('id', id)
      .select().single();
    if (error) throw error;
    const row = data as Record<string, unknown>;
    return { id: row['id'] as string, name: row['name'] as string };
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('article_families').delete().eq('id', id);
    if (error) throw error;
  }
}

// ── Brands ────────────────────────────────────────────────────────

export class SupabaseArticleBrandRepository implements IArticleBrandRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(): Promise<ArticleBrand[]> {
    const { data, error } = await this.supabase.from('article_brands').select('*').order('name');
    if (error) throw error;
    return (data ?? []).map(mapBrand);
  }

  async create(input: CreateArticleBrandInput): Promise<ArticleBrand> {
    const tenantId = await resolveCurrentTenantId(this.supabase);
    const { data, error } = await this.supabase
      .from('article_brands')
      .insert({ tenant_id: tenantId, name: input.name, image_url: input.imageUrl ?? null })
      .select().single();
    if (error) throw error;
    return mapBrand(data as Record<string, unknown>);
  }

  async update(id: string, input: Partial<CreateArticleBrandInput>): Promise<ArticleBrand> {
    const mapped: TablesUpdate<'article_brands'> = {};
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.imageUrl !== undefined) mapped['image_url'] = input.imageUrl;
    const { data, error } = await this.supabase
      .from('article_brands')
      .update(mapped)
      .eq('id', id)
      .select().single();
    if (error) throw error;
    return mapBrand(data as Record<string, unknown>);
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('article_brands').delete().eq('id', id);
    if (error) throw error;
  }
}

async function resolveCurrentTenantId(supabase: TypedSupabaseClient): Promise<string> {
  const { data, error } = await (supabase.rpc as unknown as (
    fn: string,
    args: Record<string, unknown>,
  ) => Promise<{ data: string | null; error: { message: string } | null }>)
    .call(supabase, 'current_user_tenant_id', {});

  if (error) throw error;
  if (!data) throw new Error('current_tenant_not_found');
  return data;
}

function mapBrand(r: Record<string, unknown>): ArticleBrand {
  return {
    id: r['id'] as string,
    name: r['name'] as string,
    imageUrl: (r['image_url'] as string) ?? null,
  };
}
